import React, { useState, useMemo, useEffect } from "react";
import { useAdminAuth } from "../../features/auth/AdminAuthProvider";
import { useNavigate } from "react-router-dom";
import {
  FaUserCheck,
  FaUserClock,
  FaUserLock,
  FaUserTimes,
} from "react-icons/fa";
import { BiSolidUserDetail, BiSolidDashboard } from "react-icons/bi";
import { RiLoginCircleLine, RiNodeTree } from "react-icons/ri";
import { PiGitBranchBold } from "react-icons/pi";
import { MdArrowDropDown } from "react-icons/md";
import { IoClose } from "react-icons/io5";
import { TbReport, TbReportAnalytics, TbPasswordUser } from "react-icons/tb";
import { FiLogOut } from "react-icons/fi";
import { BsDiagram3 } from "react-icons/bs";
import { GrMapLocation } from "react-icons/gr";
import { GoPasskeyFill } from "react-icons/go";
import { BASE_URL } from "../../env";
import { DL_ENABLED } from "../../config/features";
import vvLogo from "../../assets/vv-logo.png";
import vtLogo from "../../assets/VTlogo.png";
import "../../features/dashboard/screens/AdminDashboard.css";

// Icon mapping for consistent icon usage
const iconMap = {
  dashboard: BiSolidDashboard,
  master: FaUserLock,
  bl: FaUserLock,
  jlg: FaUserLock,
  reports: TbReportAnalytics,
  region: GrMapLocation,
  division: RiNodeTree,
  branch: BsDiagram3,
  loginCredentials: TbPasswordUser,
  permissions: GoPasskeyFill,
  fundingAgencies: BsDiagram3,
  dailyGoldRate: BsDiagram3,
  userDetail: BiSolidUserDetail,
  userCheck: FaUserCheck,
  userClock: FaUserClock,
  userTimes: FaUserTimes,
  report: TbReport,
};

// Helper to extract user role accurately regardless of object nesting
export const getUserType = (user) => {
  return user?.user?.userType || user?.userType || user?.user?.role || user?.role || "";
};

// Menu item data structure
const menuItems = {
  dashboard: {
    name: "Dashboard",
    path: "/Dashboard",
    icon: "dashboard",
    visible: (user) => {
      const role = getUserType(user);
      return [
        "superadmin",
        "developer",
        "Credit Manager",
        "Credit Officer",
        "Sanction Committee",
        "MIS",
        "Branch Manager",
        "Accounts Manager",
        "Field Manager",
        "fieldManager",
      ].includes(role);
    },
  },
  master: {
    name: "Master",
    icon: "master",
    visible: (user) =>
      [
        "superadmin",
        "developer",
        "Branch Manager",
        "Accounts Manager",
        "Credit Manager",
        "Credit Officer",
        "Sanction Committee",
        "MIS",
      ].includes(user?.userType),
    subItems: {
      region: {
        name: "Region",
        path: "/region",
        icon: "region",
        visible: (user) => ["superadmin", "developer", "Credit Manager", "Accounts Manager", "Credit Officer", "Sanction Committee", "MIS"].includes(user?.userType),
      },
      division: {
        name: "Division",
        path: "/division",
        icon: "division",
        visible: (user) => ["superadmin", "developer", "Credit Manager", "Accounts Manager", "Credit Officer", "Sanction Committee", "MIS"].includes(user?.userType),
      },
      branch: {
        name: "Branch",
        path: "/branch",
        icon: "branch",
        visible: (user) => ["superadmin", "developer", "Credit Manager", "Accounts Manager", "Credit Officer", "Sanction Committee", "MIS"].includes(user?.userType),
      },
      loginCredentials: {
        name: "Login Credentials",
        path: "/logincredential",
        icon: "loginCredentials",
        visible: (user) => ["superadmin", "developer"].includes(user?.userType),
      },
      permissions: {
        name: "Permissions",
        path: "/permissions",
        icon: "permissions",
        visible: (user) => ["superadmin", "developer"].includes(user?.userType),
      },
      fundingAgencies: {
        name: "Funding Agencies",
        path: "/funding/agency/list",
        state: { entity: "fundingAgencies" },
        icon: "fundingAgencies",
        visible: (user) => ["superadmin", "developer"].includes(user?.userType),
      },
      dailyGoldRate: {
        name: "Daily Gold Rate",
        path: "/dailyGoldRate",
        state: { entity: "dailyGoldRate" },
        icon: "dailyGoldRate",
        visible: (user) => ["superadmin", "developer"].includes(user?.userType),
      },
      transferCroCenterWise: {
        name: "Transfer Cro CenterWise",
        path: "/transferCroCenterWise",
        icon: "permissions",
        visible: (user) =>
          ["superadmin", "developer", "Branch Manager"].includes(
            user?.userType
          ),
      },
      transferCroMemberWise: {
        name: "Transfer Cro MemberWise",
        path: "/transferCroMemberWise",
        icon: "permissions",
        visible: (user) =>
          ["superadmin", "developer", "Branch Manager"].includes(
            user?.userType
          ),
      },
      croTransferHistoryByCenter: {
        name: "CRO Transfer History by Center",
        path: "/croTransferHistoryByCenter",
        icon: "permissions",
        visible: (user) =>
          ["superadmin", "developer", "Branch Manager"].includes(
            user?.userType
          ),
      },
      croTransferHistoryByMember: {
        name: "CRO Transfer History by Member",
        path: "/croTransferHistoryByMember",
        icon: "permissions",
        visible: (user) =>
          ["superadmin", "developer", "Branch Manager"].includes(
            user?.userType
          ),
      },
      centerTimeChange: {
        name: "Center Time Change",
        path: "/centerTimeChange",
        icon: "permissions",
        visible: (user) =>
          ["superadmin", "developer", "Branch Manager"].includes(
            user?.userType
          ),
      },
      foreclosure: {
        name: "Foreclosure",
        path: "/foreclosure",
        icon: "permissions",
        visible: (user) =>
          [
            "superadmin",
            "developer",
            "Branch Manager",
            "Accounts Manager",
          ].includes(user?.userType),
      },
      noc: {
        name: "NOC",
        path: "/noc",
        icon: "permissions",
        visible: (user) =>
          ["superadmin", "developer", "Accounts Manager"].includes(
            user?.userType
          ),
      },
    },
  },
  bl: {
    name: "BL",
    icon: "bl",
    visible: true,
    subItems: {
      memberDetails: {
        name: "Member Details",
        path: "/memberdetails",
        state: {
          manager: "",
          filterStatus: "",
          loanType: "Business Loan",
          isActive: "Member Details",
        },
        icon: "userDetail",
        visible: (user) => ["superadmin", "developer", "Credit Manager"].includes(user?.userType),
      },
      approved: {
        name: "Approved",
        path: "/memberdetails",
        state: {
          filterStatus: "approved",
          loanType: "Business Loan",
          isActive: "Approved",
        },
        icon: "userCheck",
        visible: true,
      },
      retrigger: {
        name: "Retrigger",
        path: "/memberdetails",
        state: {
          filterStatus: "retrigger",
          loanType: "Business Loan",
          isActive: "Retrigger",
        },
        icon: "userDetail",
        visible: (user) =>
          user?.userType === "developer" || !["superadmin", "developer"].includes(user?.userType),
      },
      disbursed: {
        name: (user) => (user?.userType === "Branch Manager" ? "Disbursement Pending (BM2)" : "Disbursed"),
        path: "/memberdetails",
        state: {
          filterStatus: "disbursed",
          loanType: "Business Loan",
          isActive: "Disbursement Pending",
        },
        icon: "userDetail",
        visible: (user) =>
          user?.userType === "developer" ||
          (!["superadmin", "developer"].includes(user?.userType) &&
            ["Branch Manager", "MIS", "Credit Manager", "Credit Officer", "Sanction Committee"].includes(user?.userType)),
      },
      pending: {
        name: (user) => (user?.userType === "Branch Manager" ? "Approval Pending (BM1)" : "Pending"),
        path: "/memberdetails",
        state: {
          filterStatus: "pending",
          loanType: "Business Loan",
          isActive: "Pending",
        },
        icon: "userClock",
        visible: true,
      },
      rejected: {
        name: "Rejected",
        path: "/memberdetails",
        state: {
          filterStatus: "rejected",
          loanType: "Business Loan",
          isActive: "Rejected",
        },
        icon: "userTimes",
        visible: true,
      },
      blEmiPending: {
        name: "BL Emi Pending",
        path: "/blEmiPending",
        icon: "userDetail",
        visible: (user) =>
          user?.userType === "developer" ||
          ["Branch Manager", "MIS", "Accounts Manager", "Credit Manager", "Credit Officer", "Sanction Committee"].includes(
            user?.userType
          ),
      },
    },
  },
  jlg: {
    name: "JLG",
    icon: "jlg",
    visible: true,
    subItems: {
      centerDetails: {
        name: "Center Details",
        path: "/centerDetails",
        state: { manager: "", filterStatus: "", isActive: "Center Details" },
        icon: "userDetail",
        visible: (user) => ["superadmin", "developer", "Credit Manager"].includes(user?.userType),
      },
      approved: {
        name: "Approved",
        path: "/centerDetails",
        state: { filterStatus: "approved", isActive: "Approved" },
        icon: "userDetail",
        visible: true,
      },
      retrigger: {
        name: "Retrigger",
        path: "/centerDetails",
        state: { filterStatus: "retrigger", isActive: "Retrigger" },
        icon: "userDetail",
        visible: (user) =>
          user?.userType === "developer" || !["superadmin", "developer"].includes(user?.userType),
      },
      pending: {
        name: "Pending",
        path: "/centerDetails",
        state: { filterStatus: "pending", isActive: "Pending" },
        icon: "userDetail",
        visible: true,
      },
      rejected: {
        name: "Rejected",
        path: "/centerDetails",
        state: { filterStatus: "rejected", isActive: "Rejected" },
        icon: "userDetail",
        visible: true,
      },
      disbursed: {
        name: "Disbursed",
        path: "/centerDetails",
        state: { filterStatus: "disbursed", isActive: "Disbursed" },
        icon: "userDetail",
        visible: (user) =>
          user?.userType === "developer" || ["Branch Manager", "Credit Manager", "Credit Officer", "Sanction Committee"].includes(user?.userType),
      },
      jlgEmiPending: {
        name: "JLG Emi Pending",
        path: "/jlgEmiPending",
        icon: "userDetail",
        visible: (user) =>
          user?.userType === "developer" ||
          ["Branch Manager", "MIS", "Accounts Manager", "Credit Manager", "Credit Officer", "Sanction Committee"].includes(
            user?.userType
          ),
      },
    },
  },


  gpl: {
    name: "GPL",
    icon: "bl",
    visible: true,
    subItems: {
      memberDetails: {
        name: "Member Details",
        path: "/memberdetails",
        state: {
          manager: "",
          filterStatus: "",
          loanType: "GP Loan",
          isActive: "Member Details",
        },
        icon: "userDetail",
        visible: (user) => ["superadmin", "developer", "Credit Manager"].includes(user?.userType),
      },
      approved: {
        name: "Approved",
        path: "/memberdetails",
        state: {
          filterStatus: "approved",
          loanType: "GP Loan",
          isActive: "Approved",
        },
        icon: "userCheck",
        visible: true,
      },
      retrigger: {
        name: "Retrigger",
        path: "/memberdetails",
        state: {
          filterStatus: "retrigger",
          loanType: "GP Loan",
          isActive: "Retrigger",
        },
        icon: "userDetail",
        visible: (user) =>
          user?.userType === "developer" || !["superadmin", "developer"].includes(user?.userType),
      },
      orderPlaced: {
        name: "Order Placed",
        path: "/memberdetails",
        state: {
          filterStatus: "orderPlaced",
          loanType: "GP Loan",
          isActive: "Order Placed",
        },
        icon: "userDetail",
        visible: (user) =>
          user?.userType === "developer" ||
          (!["superadmin", "developer"].includes(user?.userType) &&
            ["MIS"].includes(user?.userType)),
      },
      recheckFinally: {
        name: "Final Recheck",
        path: "/memberdetails",
        state: {
          filterStatus: "recheckFinally",
          loanType: "GP Loan",
          isActive: "Final Recheck",
        },
        icon: "userDetail",
        visible: (user) =>
          user?.userType === "developer" ||
          (!["superadmin", "developer"].includes(user?.userType) &&
            ["MIS"].includes(user?.userType)),
      },
      disbursed: {
        name: (user) => (user?.userType === "Branch Manager" ? "Disbursement Pending (BM2)" : "Disbursed"),
        path: "/memberdetails",
        state: {
          filterStatus: "disbursed",
          loanType: "GP Loan",
          isActive: "Disbursed",
        },
        icon: "userDetail",
        visible: (user) =>
          user?.userType === "developer" ||
          (!["superadmin", "developer"].includes(user?.userType) &&
            ["Branch Manager"].includes(user?.userType)),
      },
      bookingProcess: {
        name: "Booking Process",
        path: "/memberdetails",
        state: {
          filterStatus: "pending",
          loanType: "GP Loan",
          isActive: "Booking Process",
        },
        icon: "userDetail",
        visible: (user) =>
          user?.userType === "developer" ||
          (!["superadmin", "developer"].includes(user?.userType) &&
            ["Branch Manager"].includes(user?.userType)),
      },
      pending: {
        name: "Pending",
        path: "/memberdetails",
        state: {
          filterStatus: "pending",
          loanType: "GP Loan",
          isActive: "Pending",
        },
        icon: "userClock",
        visible: (user) =>
          user?.userType === "developer" || !["Branch Manager"].includes(user?.userType),
      },
      rejected: {
        name: "Rejected",
        path: "/memberdetails",
        state: {
          filterStatus: "rejected",
          loanType: "GP Loan",
          isActive: "Rejected",
        },
        icon: "userTimes",
        visible: true,
      },
      GplEmiPending: {
        name: "GP Loan Emi Pending",
        path: "/gplemipendingapproval",
        icon: "userDetail",
        visible: (user) =>
          user?.userType === "developer" ||
          ["Branch Manager", "MIS", "Accounts Manager", "Credit Manager", "Credit Officer", "Sanction Committee"].includes(
            user?.userType
          ),
      },
    },
  },


  // Daily Loan. The route is CRO -> Credit Officer -> Sanction Committee ->
  // Branch Manager (disburse) -> Accounts Manager, so there is deliberately no
  // "Order Placed", "Final Recheck" or "Booking Process" entry - those belong to
  // MIS and to the GPL gold booking, and DL has neither.
  //
  // Every sub-item MUST pass state.loanType. MemberDetails.js does
  // `const loanType = incomingLoanType || "Business Loan"`, so an entry that
  // omits it silently serves a Business Loan screen.
  //
  // The DL Reports group is defined below as `dlReports`. Both groups are gated
  // on branch access by the same branch of the filter that gates bl/blReports.
  dl: {
    name: "DL",
    icon: "bl",
    visible: true,
    subItems: {
      memberDetails: {
        name: "Member Details",
        path: "/memberdetails",
        state: {
          manager: "",
          filterStatus: "",
          loanType: "Daily Loan",
          isActive: "Member Details",
        },
        icon: "userDetail",
        visible: (user) => ["superadmin", "developer", "Credit Manager"].includes(user?.userType),
      },
      pending: {
        name: "Pending",
        path: "/memberdetails",
        state: {
          filterStatus: "pending",
          loanType: "Daily Loan",
          isActive: "Pending",
        },
        icon: "userClock",
        visible: (user) => true,
      },
      approved: {
        name: "Approved",
        path: "/memberdetails",
        state: {
          filterStatus: "approved",
          loanType: "Daily Loan",
          isActive: "Approved",
        },
        icon: "userCheck",
        visible: true,
      },
      retrigger: {
        name: "Retrigger",
        path: "/memberdetails",
        state: {
          filterStatus: "retrigger",
          loanType: "Daily Loan",
          isActive: "Retrigger",
        },
        icon: "userDetail",
        visible: (user) =>
          user?.userType === "developer" || !["superadmin", "developer"].includes(user?.userType),
      },
      disbursed: {
        name: "Disbursed",
        path: "/memberdetails",
        state: {
          filterStatus: "disbursed",
          loanType: "Daily Loan",
          isActive: "Disbursed",
        },
        icon: "userDetail",
        // Daily Loan disbursement is mobile-only for the Branch Manager - hidden
        // here on web so there's nothing to click through to. Accounts Manager
        // still needs this tab on web for DL payment.
        visible: (user) =>
          user?.userType === "developer" ||
          (!["superadmin", "developer"].includes(user?.userType) &&
            user?.userType === "Accounts Manager"),
      },
      rejected: {
        name: "Rejected",
        path: "/memberdetails",
        state: {
          filterStatus: "rejected",
          loanType: "Daily Loan",
          isActive: "Rejected",
        },
        icon: "userTimes",
        visible: true,
      },
    },
  },


  blReports: {
    name: "BL Reports",
    icon: "reports",
    visible: true,
    subItems: {
      futureDemandReport: {
        name: "Future Demand Report",
        path: "/futureDemandReport",
        icon: "report",
        permission: "futureDemandReport",
      },
      loanDisbursedReport: {
        name: "Loan Disbursed Report",
        path: "/loanDisbursedReport",
        icon: "report",
        permission: "loanDisbursedReport",
      },
      masterDataReport: {
        name: "Master Data Report",
        path: "/masterDataReport",
        icon: "report",
        permission: "masterDataReport",
      },
      clientProspectReport: {
        name: "Client Prospect Report",
        path: "/clientProspectReport",
        icon: "report",
        permission: "clientProspectReport",
      },
      outstandingReport: {
        name: "Outstanding Report",
        path: "/outstandingReport",
        icon: "report",
        permission: "outstandingReport",
      },
      overDueReport: {
        name: "OverDue Report",
        path: "/overDueReport",
        icon: "report",
        permission: "overDueReport",
      },
      parReport: {
        name: "PAR Report",
        path: "/parReport",
        icon: "report",
        permission: "parReport",
      },
      securityDepositReport: {
        name: "Security Deposit Report",
        path: "/securityDepositReport",
        icon: "report",
        permission: "securityDepositReport",
      },
      processingChargeReport: {
        name: "Processing Charge Report",
        path: "/processingChargeReport",
        icon: "report",
        permission: "processingChargeReport",
      },
      collectionReport: {
        name: "Collection Report",
        path: "/collectionReport",
        icon: "report",
        permission: "collectionReport",
      },
      demandVsCollectionReport: {
        name: "Demand Vs Collection Report",
        path: "/demandVsCollectionReport",
        icon: "report",
        permission: "demandVsCollectionReport",
      },
      rejectReport: {
        name: "Reject Report",
        path: "/rejectReport",
        icon: "report",
        permission: "rejectReport",
      },
      accountStatementReport: {
        name: "Account Statement Report",
        path: "/accountStatementReport",
        icon: "report",
        permission: "accountStatementReport",
      },
      insuranceReport: {
        name: "Insurance Report",
        path: "/insuranceReport",
        icon: "report",
        permission: "insuranceReport",
      },
      interestReport: {
        name: "Interest Report",
        path: "/interestReport",
        icon: "report",
        permission: "interestReport",
      },
      processingFeeSummaryReport: {
        name: "Processing Fee Summary Report",
        path: "/processingFeeSummaryReport",
        icon: "report",
        permission: "processingFeeSummaryReport",
      },
    },
  },
  jlgReports: {
    name: "JLG Reports",
    icon: "reports",
    visible: true,
    subItems: {
      jlgFutureDemandReport: {
        name: "JLG Future Demand Report",
        path: "/jlgFutureDemandReport",
        icon: "report",
        permission: "jlgFutureDemandReport",
      },
      jlgLoanDisbursedReport: {
        name: "JLG Loan Disbursed Report",
        path: "/jlgLoanDisbursedReport",
        icon: "report",
        permission: "jlgLoanDisbursedReport",
      },
      jlgMasterDataReport: {
        name: "JLG Master Data Report",
        path: "/jlgMasterDataReport",
        icon: "report",
        permission: "jlgMasterDataReport",
      },
      jlgClientProspectReport: {
        name: "JLG Client Prospect Report",
        path: "/jlgClientProspectReport",
        icon: "report",
        permission: "jlgClientProspectReport",
      },
      jlgOutstandingReport: {
        name: "JLG Outstanding Report",
        path: "/jlgOutstandingReport",
        icon: "report",
        permission: "jlgOutstandingReport",
      },
      jlgOverDueReport: {
        name: "JLG OverDue Report",
        path: "/jlgOverDueReport",
        icon: "report",
        permission: "jlgOverDueReport",
      },
      jlgParReport: {
        name: "JLG PAR Report",
        path: "/jlgParReport",
        icon: "report",
        permission: "jlgParReport",
      },
      jlgSecurityDepositReport: {
        name: "JLG Security Deposit Report",
        path: "/jlgSecurityDepositReport",
        icon: "report",
        permission: "jlgSecurityDepositReport",
      },
      jlgProcessingChargeReport: {
        name: "JLG Processing Charge Report",
        path: "/jlgProcessingChargeReport",
        icon: "bundling",
        permission: "jlgProcessingChargeReport",
      },
      jlgCollectionReport: {
        name: "JLG Collection Report",
        path: "/jlgCollectionReport",
        icon: "report",
        permission: "jlgCollectionReport",
      },
      jlgDemandCollectionVsReport: {
        name: "JLG Demand Vs Collection Report",
        path: "/jlgDemandVsCollectionReport",
        icon: "report",
        permission: "jlgDemandVsCollectionReport",
      },
      jlgRejectReport: {
        name: "JLG Reject Report",
        path: "/jlgRejectReport",
        icon: "report",
        permission: "jlgRejectReport",
      },
      jlgInsuranceReport: {
        name: "JLG Insurance Report",
        path: "/jlgInsuranceReport",
        icon: "report",
        permission: "jlgInsuranceReport",
      },
      jlgInterestReport: {
        name: "JLG Interest Report",
        path: "/jlgInterestReport",
        icon: "report",
        permission: "jlgInterestReport",
      },
      accountStatementReport: {
        name: "Account Statement Report",
        path: "/jlgAccountstatement",
        icon: "report",
        permission: "accountStatementReport",
      },
      jlgCollectionSheetReport: {
        name: "JLG Collection Sheet Report",
        path: "/jlgCollectionSheetReport",
        icon: "report",
        permission: "jlgCollectionSheetReport",
      },
      jlgProcessingFeeSummaryReport: {
        name: "JLG Processing Fee Summary Report",
        path: "/jlgProcessingFeeSummaryReport",
        icon: "report",
        permission: "jlgProcessingFeeSummaryReport",
      },
    },
  },
  // Daily Loan reports. The permission keys match the rows seeded by migration
  // 20260905140000-seed-dl-report-permissions.js, which copied each report's
  // access from its Business Loan equivalent. A key that does not match a
  // reports.reportName row hides the entry from every branch user without any
  // error, so these three strings are load bearing.
  //
  // Gating: the filter below treats "dlReports" exactly like "blReports" and
  // shows it only to branches with Daily Loan access.
  dlReports: {
    name: "DL Reports",
    icon: "reports",
    visible: true,
    subItems: {
      dlMasterDataReport: {
        name: "DL Master Report",
        path: "/dlMasterDataReport",
        icon: "report",
        permission: "dlMasterDataReport",
      },
      dlOutstandingReport: {
        name: "DL Outstanding Report",
        path: "/dlOutstandingReport",
        icon: "report",
        permission: "dlOutstandingReport",
      },
      dlCollectionReport: {
        name: "DL Collection Report",
        path: "/dlCollectionReport",
        icon: "report",
        permission: "dlCollectionReport",
      },
    },
  },
  gplReports: {
    name: "GPL Reports",
    icon: "reports",
    visible: true,
    subItems: {
      gpl_future_demand_report: {
        name: "GPL Future Demand Report",
        path: "/gplFutureDemandReport",
        icon: "report",
        permission: "gpl_future_demand_report",
      },
      gpl_loan_disbursed_report: {
        name: "GPL Loan Disbursed Report",
        path: "/gplLoanDisbursedReport",
        icon: "report",
        permission: "gpl_loan_disbursed_report",
      },
      gpl_master_data_report: {
        name: "GPL Master Data Report",
        path: "/gplMasterDataReport",
        icon: "report",
        permission: "gpl_master_data_report",
      },
      gpl_client_prospect_report: {
        name: "GPL Client Prospect Report",
        path: "/gplClientProspectReport",
        icon: "report",
        permission: "gpl_client_prospect_report",
      },
      gpl_outstanding_report: {
        name: "GPL Outstanding Report",
        path: "/gplOutstandingReport",
        icon: "report",
        permission: "gpl_outstanding_report",
      },
      gpl_over_due_report: {
        name: "GPL OverDue Report",
        path: "/gplOverDueReport",
        icon: "report",
        permission: "gpl_over_due_report",
      },
      gpl_par_report: {
        name: "GPL PAR Report",
        path: "/gplParReport",
        icon: "report",
        permission: "gpl_par_report",
      },
      gpl_security_deposite_report: {
        name: "GPL Security Deposit Report",
        path: "/gplSecurityDepositReport",
        icon: "report",
        permission: "gpl_security_deposite_report",
      },
      gpl_processing_charge_report: {
        name: "GPL Processing Charge Report",
        path: "/gplProcessingChargeReport",
        icon: "report",
        permission: "gpl_processing_charge_report",
      },
      gpl_collection_report: {
        name: "GPL Collection Report",
        path: "/gplCollectionReport",
        icon: "report",
        permission: "gpl_collection_report",
      },
      gpl_demand_vs_collection_report: {
        name: "GPL Demand Vs Collection Report",
        path: "/gplDemandVsCollectionReport",
        icon: "report",
        permission: "gpl_demand_vs_collection_report",
      },
      gpl_reject_report: {
        name: "GPL Reject Report",
        path: "/gplRejectReport",
        icon: "report",
        permission: "gpl_reject_report",
      },
      gpl_insurence_report: {
        name: "GPL Insurance Report",
        path: "/gplInsuranceReport",
        icon: "report",
        permission: "gpl_insurence_report",
      },
      gpl_interest_report: {
        name: "GPL Interest Report",
        path: "/gplInterestReport",
        icon: "report",
        permission: "gpl_interest_report",
      },
      accountStatementReport: {
        name: "Account Statement Report",
        path: "/getAccountStatement",
        icon: "report",
        permission: "accountStatementReport",
      },
      gpl_booking_report: {
        name: "GPL Booking Report",
        path: "/gplBookingReport",
        icon: "report",
        permission: "gpl_booking_report",
      },
      gpl_processing_fee_summary_report: {
        name: "GPL Processing Fee Summary Report",
        path: "/gplProcessingFeeSummaryReport",
        icon: "report",
        permission: "gpl_processing_summary_report",
      },
    },
  },


  superadminReports: {
    name: "Superadmin Reports",
    icon: "reports",
    visible: (user) => ["superadmin", "developer", "Credit Manager", "Accounts Manager", "Credit Officer", "Sanction Committee", "MIS"].includes(user?.userType),
    subItems: {
      summaryReport: {
        name: "Summary Report",
        path: "/summaryReport",
        icon: "report",
        permission: "summaryReport",
      },
      revenueRoiReport: {
        name: "Revenue - ROI Report",
        path: "/revenueRoiReport",
        icon: "report",
        permission: "revenueRoiReport",
      },
      revenueProcessingFeeReport: {
        name: "Revenue - Processing Fee Report",
        path: "/revenueProcessingFeeReport",
        icon: "report",
        permission: "revenueProcessingFeeReport",
      },
    }
  },
  reports: {
    name: "Reports",
    icon: "reports",
    visible: true,
    subItems: {
      foreclosureReport: {
        name: "Foreclosure Report",
        path: "/foreclosureReport",
        icon: "report",
        permission: "foreclosureReport",
      },
      eucReport: {
        name: "EUC Report",
        path: "/eucReport",
        icon: "report",
        permission: "eucReport",
      },
      croTransferByCenterWiseReport: {
        name: "CRO Transfer by Center Wise Report",
        path: "/croTransferByCenterWiseReport",
        icon: "report",
        permission: "croTransferByCenterWiseReport",
      },
      croTransferByMemberWiseReport: {
        name: "CRO Transfer by Member Wise Report",
        path: "/croTransferByMemberWiseReport",
        icon: "report",
        permission: "croTransferByMemberWiseReport",
      },
    },
  },
};

const resolveName = (item, user) => {
  if (typeof item.name === "function") {
    return item.name(user);
  }
  return item.name;
};

const MenuItem = ({
  item,
  isActive,
  setIsActive,
  navigate,
  adminUser,
  isOpen,
  toggleDropdown,
  onItemClick,
}) => {
  const userRole = getUserType(adminUser);
  const normalizedUser = {
    ...adminUser,
    userType: userRole,
    role: userRole,
    ...(adminUser?.user || {}),
  };
  const IconComponent = iconMap[item.icon];

  return (
    <div className={`sidebar-list-item ${isActive === resolveName(item, normalizedUser) ? "active" : ""}`}>
      <div
        className="menu-item-row"
        onClick={
          item.subItems
            ? toggleDropdown
            : () => {
              setIsActive(resolveName(item, normalizedUser));
              navigate(item.path, { state: item.state });
              onItemClick();
            }
        }
      >
        <div className="menu-item-content">
          {IconComponent && <IconComponent className="icon" />}
          <span>{resolveName(item, normalizedUser)}</span>
        </div>
        {item.subItems && (
          <div
            className="dropdown-arrow-wrapper"
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown();
            }}
          >
            <MdArrowDropDown
              className="menu-item-arrow icon"
              style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          </div>
        )}
      </div>
      {isOpen && item.subItems && (
        <ul style={{ listStyle: "none" }} className="nested-list-master">
          {Object.entries(item.subItems)
            .filter(([key], index, self) => {
              // Prevent duplicate keys in subItems
              return self.findIndex(([k]) => k === key) === index;
            })
            .map(([key, subItem]) => {
              const isHOUser = ["superadmin", "developer", "Credit Manager", "Accounts Manager", "Credit Officer", "Sanction Committee", "MIS"].includes(userRole);
              const isVisible = (isHOUser && subItem.permission)
                ? true
                : subItem.permission
                  ? adminUser?.permissions?.some(
                    (p) => p.reportName === subItem.permission && p.view === true
                  )
                  : typeof subItem.visible === "function"
                    ? subItem.visible(normalizedUser)
                    : subItem.visible !== false;

              if (!isVisible) return null;

              const SubIconComponent = iconMap[subItem.icon];
              return (
                <li
                  key={key}
                  className={`nested-list-master-item ${isActive === resolveName(subItem, adminUser) ? "active" : ""
                    } p-2`}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log("Sub-item clicked:", subItem.name);
                    setIsActive(resolveName(subItem, adminUser));
                    onItemClick();
                    navigate(subItem.path, { state: subItem.state });
                  }}
                  style={{ borderRadius: 5 }}
                >
                  <span className="">
                    {SubIconComponent && <SubIconComponent className="icon" />}{" "}
                    {resolveName(subItem, adminUser)}
                  </span>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
};

const Sidebar = () => {
  const { adminUser, adminLogout, openSidebarToggle, toggleSidebar, accessTypes = [] } = useAdminAuth();

  const OpenSidebar = () => {
    toggleSidebar();
  };
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState("Dashboard");
  const [openDropdowns, setOpenDropdowns] = useState(() => {
    try {
      const saved = sessionStorage.getItem("sidebar_dropdowns");
      if (saved) return JSON.parse(saved);
    } catch (e) {
      // ignore
    }
    return {
      master: false,
      bl: false,
      jlg: false,
      blReports: false,
      dlReports: false,
      jlgReports: false,
      superadminReports: false,
      reports: false,
      gpl: false,
      gplReports: false,
      dl: false,
    };
  });

  const normalizeAccessType = (accessType) => {
    if (!accessType) return "";
    return accessType.toLowerCase().replace(/\s+/g, "");
  };

  const hasLoanAccess = (loanTypes) => {
    const typesToCheck = Array.isArray(loanTypes) ? loanTypes : [loanTypes];
    return accessTypes.some((accessType) => {
      const normalizedAccess = normalizeAccessType(accessType);
      if (!normalizedAccess) return false;
      return typesToCheck.some((loanType) => {
        const normalizedLoan = normalizeAccessType(loanType);
        if (!normalizedLoan) return false;
        return (
          normalizedAccess.includes(normalizedLoan) ||
          normalizedLoan.includes(normalizedAccess) ||
          (normalizedAccess.includes("business") && normalizedLoan.includes("bl")) ||
          (normalizedAccess.includes("bl") && normalizedLoan.includes("business")) ||
          (normalizedAccess.includes("gp") && normalizedLoan.includes("gold")) ||
          (normalizedAccess.includes("gold") && normalizedLoan.includes("gp")) ||
          (normalizedAccess.includes("gpl") && normalizedLoan.includes("gp"))
        );
      });
    });
  };

  const toggleDropdown = (dropdown) => {
    setOpenDropdowns((prev) => {
      const next = {
        ...prev,
        [dropdown]: !prev[dropdown],
      };
      try {
        sessionStorage.setItem("sidebar_dropdowns", JSON.stringify(next));
      } catch (e) {
        // ignore
      }
      return next;
    });
  };

  const handleLogoutClick = () => {
    localStorage.removeItem("adminUser");
    adminLogout();
    navigate("/");
    window.location.reload(); // Force reload to ensure a clean state
  };

  const handleDashboard = () => {
    setIsActive("Dashboard");
    navigate("/Dashboard");
    if (window.innerWidth <= 992) {
      OpenSidebar();
    }
  };

  const filteredMenuItems = useMemo(() => {
    const seen = new Set();
    const userRole = getUserType(adminUser);
    const normalizedUser = {
      ...adminUser,
      userType: userRole,
      role: userRole,
      ...(adminUser?.user || {}),
    };

    return Object.entries(menuItems).filter(([key, item]) => {
      if (seen.has(key)) return false;
      let shouldShow = false;

      if (userRole === "developer") {
        shouldShow = typeof item.visible === "function"
          ? item.visible(normalizedUser)
          : item.visible !== false;
      } else if (key === "bl" || key === "blReports") {
        shouldShow = hasLoanAccess(["Business Loan", "BL", "Business"]);
      } else if (key === "jlg" || key === "jlgReports") {
        shouldShow = hasLoanAccess(["JLG", "Joint Liability Group"]);
      } else if (key === "gpl" || key === "gplReports") {
        shouldShow = hasLoanAccess(["GP Loan", "GPL", "Gold Loan", "GP", "Gold"]);
      } else if (key === "dl" || key === "dlReports") {
        // Without this branch the DL group falls to the else below, which shows a
        // group regardless of branch access - so DL would appear for every branch
        // in the system. hasLoanAccess does fuzzy substring matching, and
        // "dailyloan" collides with none of bl / gp / gold / jlg / business.
        //
        // DL_ENABLED is a release-time freeze switch (frontend/src/config/features.js) -
        // DL is still in testing, so this stays false until it ships for real.
        shouldShow = DL_ENABLED && hasLoanAccess(["Daily Loan", "DL", "Daily"]);
      } else {
        shouldShow = typeof item.visible === "function"
          ? item.visible(normalizedUser)
          : item.visible !== false;
      }

      if (shouldShow) seen.add(key);
      return shouldShow;
    });
  }, [adminUser, accessTypes]);

  return (
    <aside
      id="sidebar"
      className={
        openSidebarToggle ? "sidebar-responsive" : "sidebar-responsive-set"
      }
    >
      <IoClose
        className="icon closeIconSideNav"
        onClick={OpenSidebar}
        size={24}
      />
      <div className="sidebar-title text-center" onClick={handleDashboard}>
        <div className="profile-sidebar">
          <img
            src={vvLogo}
            className="img-radius"
            alt="User-Profile-pic"
          />
        </div>
        <div className="sidebar-brand">
          <span>{adminUser?.user?.username}</span>
        </div>
      </div>

      <div className="sidebar-list">
        {filteredMenuItems.map(([key, item]) => (
          <MenuItem
            key={key}
            item={item}
            isActive={isActive}
            setIsActive={setIsActive}
            navigate={navigate}
            adminUser={adminUser}
            isOpen={openDropdowns[key]}
            toggleDropdown={() => toggleDropdown(key)}
            onItemClick={() => {
              if (window.innerWidth <= 992) {
                OpenSidebar();
              }
            }}
          />
        ))}
      </div>

      <div className="vt-logo-container">
        <img src={vtLogo} alt="VT Logo" style={{ opacity: '0.8' }} />
      </div>

      <div className="logout" onClick={handleLogoutClick}>
        <div className="logout2">
          <span>
            <FiLogOut />
          </span>
          <span>Logout</span>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;